import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import Swal from 'sweetalert2';
import { getOrgPermissions } from '../../utils/permissions';
import { isSoc2 as sharedIsSoc2 } from '../../utils/tsc';
import i18n from '../../i18n';

// ── Constants ─────────────────────────────────────────────────────────────────
// Vendor metadata (icons, names, tokenLabels) now lives in the shared OrgAdmin
// catalogue at components/Vendor/VendorCards.js, which the CPA workspace embeds
// for vendor onboarding. Only the status-pill style tables for non-vendor entities
// (policies, documents) remain here.

const POLICY_STATUS_STYLE = {
  PUBLISHED:    { bg: '#d1fae5', color: '#065f46' },
  ACTIVE:       { bg: '#dbeafe', color: '#1e40af' },
  DRAFT:        { bg: '#f1f5f9', color: '#64748b' },
  UNDER_REVIEW: { bg: '#fef3c7', color: '#92400e' },
};

const DOC_STATUS_STYLE = {
  IN_FORCE:   { bg: '#d1fae5', color: '#065f46' },
  ACCEPTED:   { bg: '#dbeafe', color: '#1e40af' },
  SUBMITTED:  { bg: '#fef3c7', color: '#92400e' },
  DRAFT:      { bg: '#f1f5f9', color: '#64748b' },
};

function fmt(dt) {
  if (!dt) return '—';
  try { return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
}

// ── Vendors / Integrations tab ────────────────────────────────────────────────
// Reuses the OrgAdmin vendor catalogue (Vendor/VendorCards) and setup wizard
// (VendorDetails/VendorDetailsPage). The CPA gets the SAME 20-vendor guided UX
// as OrgAdmins; the wizard reads the client orgId from sessionStorage and POSTs
// to /partner/context/clients/{orgId}/vendors/connect.
function IntegrationsTab({ org }) {
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  const [assets,      setAssets]      = useState(null); // AssetResponseDTO from /assets
  const [loading,     setLoading]     = useState(true);
  const [syncing,     setSyncing]     = useState({}); // { [connectionId]: true }
  const [Catalogue,   setCatalogue]   = useState(null);

  const loadConnections = useCallback(() => {
    return axiosInstance.get(`/partner/context/clients/${org.id}/vendors/connections`)
      .then(r => setConnections(r.data || []))
      .catch(() => setConnections([]));
  }, [org.id]);

  const loadAssets = useCallback(() => {
    // Backend paginates the asset list. Ask for a generous page so the table shows
    // every category (S3 / EC2 / Lambda / IAM / RDS / ELB) instead of just the first
    // few S3 buckets ordered by id ASC.
    return axiosInstance.get(`/partner/context/clients/${org.id}/assets?page=0&size=500`)
      .then(r => setAssets(r.data?.[0] || null))
      .catch(() => setAssets(null));
  }, [org.id]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([loadConnections(), loadAssets()]).finally(() => setLoading(false));
  }, [loadConnections, loadAssets]);

  useEffect(() => { load(); }, [load]);

  // Lazy-load the catalogue so OrgAdmin code is only fetched when the CPA opens this tab
  useEffect(() => {
    import('../Vendor/VendorCards').then(mod => setCatalogue(() => mod.default));
  }, []);

  const handleDisconnect = async (id) => {
    const r = await Swal.fire({ title: t('CpaClientWorkspace.disconnectVendorTitle'), icon: 'warning',
      showCancelButton: true, confirmButtonText: t('CpaClientWorkspace.disconnect'), confirmButtonColor: '#ef4444' });
    if (!r.isConfirmed) return;
    try {
      await axiosInstance.delete(`/partner/context/clients/${org.id}/vendors/connections/${id}`);
      load();
    } catch { Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.error'), text: t('CpaClientWorkspace.failedToDisconnect') }); }
  };

  const handleSync = async (connectionId) => {
    setSyncing(s => ({ ...s, [connectionId]: true }));

    // Discovery can take several minutes (multi-region × multi-service). Poll the
    // assets list every 5 seconds while the sync is in flight so the table fills
    // in live instead of waiting for the request to fully return.
    const pollId = setInterval(loadAssets, 5000);

    try {
      const r = await axiosInstance.post(
        `/partner/context/clients/${org.id}/vendors/connections/${connectionId}/sync`,
        null,
        { timeout: 10 * 60 * 1000 }   // allow up to 10 min for the full multi-region scan
      );
      Swal.fire({ icon: 'success', title: t('CpaClientWorkspace.syncComplete'), text: r.data?.message || t('CpaClientWorkspace.assetsRefreshed'), timer: 2500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.syncFailed'), text: e?.response?.data?.message || e?.response?.data?.error || t('CpaClientWorkspace.failedToTriggerDiscovery') });
    } finally {
      clearInterval(pollId);
      load();
      setSyncing(s => { const n = { ...s }; delete n[connectionId]; return n; });
    }
  };

  const totalAssets = assets?.totalCount || 0;
  const awsAssets   = assets?.awsAssets   || [];
  const gcpAssets   = assets?.gcpAssets   || [];
  const ghRepos     = assets?.gitHubRepos || [];
  const azureAssets = assets?.azureAssets || [];

  // Roll-up by description (matches AWS asset type) so the demo shows every category
  const awsByType = awsAssets.reduce((m, a) => {
    const k = (a.description || 'OTHER').toUpperCase();
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {});

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h4 style={{ margin: 0, fontWeight: 700 }}>{t('CpaClientWorkspace.vendorIntegrationsAndAssets')}</h4>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.87rem' }}>
          {t('CpaClientWorkspace.vendorIntegrationsSubtitle', { name: org.name })}
        </p>
      </div>

      {/* Connected vendors with sync action */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', marginBottom: 10 }}>
          <i className="fas fa-check-circle" style={{ marginRight: 6, color: '#10b981' }} />
          {t('CpaClientWorkspace.connectedVendors', { count: connections.length })}
        </div>
        {loading ? (
          <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.loading')}</div>
        ) : connections.length === 0 ? (
          <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10,
            padding: 16, color: '#64748b', fontSize: '0.85rem' }}>
            {t('CpaClientWorkspace.noVendorsConnected')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {connections.map(c => (
              <div key={c.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <i className="fas fa-plug" style={{ fontSize: 18, color: '#10b981' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{c.vendorType}</div>
                  <div style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
                    {c.status} · {c.lastSyncedAt ? t('CpaClientWorkspace.lastSynced', { date: fmt(c.lastSyncedAt) }) : t('CpaClientWorkspace.neverSynced')}
                  </div>
                </div>
                <button onClick={() => handleSync(c.id)} disabled={syncing[c.id]}
                  style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8,
                    padding: '6px 12px', fontSize: '0.82rem', fontWeight: 600,
                    cursor: syncing[c.id] ? 'wait' : 'pointer' }}>
                  {syncing[c.id]
                    ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.syncing')}</>
                    : <><i className="fas fa-sync" /> {t('CpaClientWorkspace.discoverAssets')}</>}
                </button>
                <button onClick={() => handleDisconnect(c.id)}
                  style={{ background: '#fee2e2', border: 'none', borderRadius: 8, padding: '6px 10px',
                    cursor: 'pointer', color: '#ef4444', fontSize: 13 }} title={t('CpaClientWorkspace.disconnect')}>
                  <i className="fas fa-unlink" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Discovered assets — same shape as OrgAdmin /api/assets response */}
      {totalAssets > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', marginBottom: 10 }}>
            <i className="fas fa-cubes" style={{ marginRight: 6, color: '#3b82f6' }} />
            {t('CpaClientWorkspace.discoveredAssets', { count: totalAssets })}
          </div>

          {/* Asset count chips — provider rollup + AWS subtype breakdown */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {[
              { label: t('CpaClientWorkspace.awsTotal'), count: assets.totalAwsAssetsCount   || 0, icon: 'fab fa-aws',     color: '#FF9900' },
              { label: 'GCP',       count: assets.totalGcpAssetsCount   || 0, icon: 'fab fa-google',  color: '#4285F4' },
              { label: 'GitHub',    count: assets.totalGitHubReposCount || 0, icon: 'fab fa-github',  color: '#1f2937' },
              { label: 'Azure',     count: assets.totalAzureAssetsCount || 0, icon: 'fab fa-microsoft', color: '#0089D6' },
            ].filter(c => c.count > 0).map(c => (
              <div key={c.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 100 }}>
                <i className={c.icon} style={{ fontSize: 18, color: c.color }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{c.count}</div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{c.label}</div>
                </div>
              </div>
            ))}
            {Object.entries(awsByType).sort((a,b) => b[1]-a[1]).map(([type, count]) => (
              <div key={type} style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10,
                padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#9a3412' }}>{count}</div>
                  <div style={{ fontSize: '0.72rem', color: '#9a3412', textTransform: 'capitalize' }}>{type.toLowerCase()}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Asset table */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>{t('CpaClientWorkspace.colAsset')}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>{t('CpaClientWorkspace.colType')}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>{t('CpaClientWorkspace.colOwner')}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>{t('CpaClientWorkspace.colRegion')}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>{t('CpaClientWorkspace.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {awsAssets.map((a, i) => (
                  <tr key={`aws-${i}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                      <i className="fab fa-aws" style={{ marginRight: 8, color: '#FF9900' }} />
                      {a.assetName}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.description}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.owner}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.region}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {(a.encrypted ?? a.isEncrypted) ? <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>{t('CpaClientWorkspace.encrypted')}</span>
                                     : <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>{t('CpaClientWorkspace.unencrypted')}</span>}
                    </td>
                  </tr>
                ))}
                {gcpAssets.map((a, i) => (
                  <tr key={`gcp-${i}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                      <i className="fab fa-google" style={{ marginRight: 8, color: '#4285F4' }} />
                      {a.assetName}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.description}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.owner}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.region}</td>
                    <td style={{ padding: '10px 14px' }}>—</td>
                  </tr>
                ))}
                {ghRepos.map((r, i) => (
                  <tr key={`gh-${i}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                      <i className="fab fa-github" style={{ marginRight: 8, color: '#1f2937' }} />
                      {r.name}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{t('CpaClientWorkspace.githubRepository')}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{r.fullName}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>—</td>
                    <td style={{ padding: '10px 14px' }}>
                      {r.isPrivate ? <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>{t('CpaClientWorkspace.private')}</span>
                                   : <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>{t('CpaClientWorkspace.public')}</span>}
                    </td>
                  </tr>
                ))}
                {azureAssets.map((a, i) => (
                  <tr key={`azure-${i}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                      <i className="fab fa-microsoft" style={{ marginRight: 8, color: '#0089D6' }} />
                      {a.assetName}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.resourceType}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.description || '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.region}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {a.active ? <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>{t('CpaClientWorkspace.active')}</span>
                                : <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>{t('CpaClientWorkspace.inactive')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty-state hint when connections exist but no assets yet */}
      {!loading && connections.length > 0 && totalAssets === 0 && (
        <div style={{ background: '#f0f9ff', border: '1px dashed #bae6fd', borderRadius: 10,
          padding: 14, color: '#075985', fontSize: '0.85rem', marginBottom: 22 }}>
          <i className="fas fa-info-circle" style={{ marginRight: 6 }} />
          {t('CpaClientWorkspace.noAssetsHintBefore')} <strong>{t('CpaClientWorkspace.discoverAssets')}</strong> {t('CpaClientWorkspace.noAssetsHintAfter')}
        </div>
      )}

      {/* Shared vendor catalogue — same UI as OrgAdmin role */}
      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 18 }}>
        {!Catalogue ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /> {t('CpaClientWorkspace.loadingVendorCatalogue')}
          </div>
        ) : (
          <Catalogue orgContext={{ id: org.id, name: org.name }} embedded />
        )}
      </div>
    </div>
  );
}

// ── Audits tab ────────────────────────────────────────────────────────────────

function AuditsTab({ org }) {
  const { t } = useTranslation();
  const [audits,      setAudits]      = useState([]);
  const [frameworks,  setFrameworks]  = useState([]);
  const [users,       setUsers]       = useState([]);
  const [vendors,     setVendors]     = useState([]);  // connected vendor types for this org
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [frameworkId, setFrameworkId] = useState('');
  const [auditorId,   setAuditorId]   = useState('');
  const [extAuditor,  setExtAuditor]  = useState('');
  const [auditorMode, setAuditorMode] = useState('internal');
  const [saving,      setSaving]      = useState(false);

  // SOC 2 metadata + scheduling for the new audit
  const [auditType,       setAuditType]       = useState('SOC2_TYPE_2');
  const [asOfDate,        setAsOfDate]        = useState('');
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodEndDate,   setPeriodEndDate]   = useState('');
  const [scheduledRunAt,  setScheduledRunAt]  = useState('');

  // Run + Trust Certificate flows
  const [runningId,       setRunningId]       = useState(null);
  const [certificate,     setCertificate]     = useState(null); // { audit, data }

  const { canCreateAudit } = getOrgPermissions();
  const selectedFw = frameworks.find(f => String(f.id) === String(frameworkId));
  const isSoc2 = sharedIsSoc2(selectedFw);

  const load = useCallback(async () => {
    setLoading(true);
    const [audRes, fwRes, vnRes] = await Promise.allSettled([
      axiosInstance.get(`/partner/context/clients/${org.id}/audits`),
      axiosInstance.get(`/partner/cpa/orgs/${org.id}/frameworks`),
      axiosInstance.get(`/partner/context/clients/${org.id}/vendors/connections`),
    ]);
    if (audRes.status === 'fulfilled') setAudits(audRes.value.data || []);
    if (fwRes.status === 'fulfilled')  setFrameworks(fwRes.value.data?.map(f => ({ id: f.id, displayName: f.display_name })) || []);
    if (vnRes.status === 'fulfilled')  setVendors((vnRes.value.data || []).map(v => (v.vendorType || '').toUpperCase()));
    setLoading(false);
  }, [org.id]);

  const loadUsers = async () => {
    try {
      const r = await axiosInstance.get('/partner/cpa/org-auditors');
      setUsers(r.data?.map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}`, org: u.organization_name })) || []);
    } catch { setUsers([]); }
  };

  const openCreate = async () => {
    setShowCreate(true);
    setFrameworkId(''); setAuditorId(''); setExtAuditor(''); setAuditorMode('internal');
    setAuditType('SOC2_TYPE_2'); setAsOfDate(''); setPeriodStartDate(''); setPeriodEndDate(''); setScheduledRunAt('');
    await loadUsers();
  };

  const handleCreate = async () => {
    if (!frameworkId) { Swal.fire({ title: t('CpaClientWorkspace.missingField'), text: t('CpaClientWorkspace.pleaseSelectFramework'), icon: 'warning' }); return; }
    if (auditorMode === 'external' && !extAuditor.trim()) { Swal.fire({ title: t('CpaClientWorkspace.missingField'), text: t('CpaClientWorkspace.pleaseEnterExternalAuditor'), icon: 'warning' }); return; }
    if (auditorMode === 'internal' && !auditorId) { Swal.fire({ title: t('CpaClientWorkspace.missingField'), text: t('CpaClientWorkspace.pleaseSelectAuditor'), icon: 'warning' }); return; }
    if (isSoc2 && auditType === 'SOC2_TYPE_1' && !asOfDate) {
      Swal.fire({ title: t('CpaClientWorkspace.missingField'), text: t('CpaClientWorkspace.type1RequiresAsOfDate'), icon: 'warning' }); return;
    }
    if (isSoc2 && auditType === 'SOC2_TYPE_2' && (!periodStartDate || !periodEndDate)) {
      Swal.fire({ title: t('CpaClientWorkspace.missingField'), text: t('CpaClientWorkspace.type2RequiresPeriod'), icon: 'warning' }); return;
    }
    setSaving(true);
    try {
      const params = { frameworkId, orgId: org.id };
      if (auditorMode === 'external') params.auditorName = extAuditor.trim();
      else params.auditorId = auditorId;
      const body = {};
      if (isSoc2) {
        body.auditType = auditType;
        if (auditType === 'SOC2_TYPE_1') body.asOfDate = asOfDate;
        if (auditType === 'SOC2_TYPE_2') {
          body.periodStartDate = periodStartDate;
          body.periodEndDate   = periodEndDate;
        }
      }
      if (scheduledRunAt) body.scheduledRunAt = scheduledRunAt;
      await axiosInstance.post('/audits/create', body, { params });
      setShowCreate(false);
      load();
    } catch (e) {
      Swal.fire({ title: t('CpaClientWorkspace.error'), text: e?.response?.data?.message || t('CpaClientWorkspace.failedToCreateAudit'), icon: 'error' });
    } finally { setSaving(false); }
  };

  useEffect(() => { load(); }, [load]);

  // Run all tests in an audit; show progress while the request is in flight.
  const handleRun = async (audit) => {
    setRunningId(audit.id);
    try {
      const r = await axiosInstance.post(`/partner/context/clients/${org.id}/audits/${audit.id}/run`,
        null, { timeout: 5 * 60 * 1000 });
      Swal.fire({ icon: 'success', title: t('CpaClientWorkspace.auditRunComplete'),
        text: r.data?.message || t('CpaClientWorkspace.testsPassed', { passed: r.data?.passedTests || 0, total: r.data?.totalTests || 0 }),
        timer: 2500, showConfirmButton: false });
      load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.runFailed'),
        text: e?.response?.data?.message || t('CpaClientWorkspace.failedToRunTests') });
    } finally {
      setRunningId(null);
    }
  };

  // Fetch the trust certificate / gap report and pop a modal.
  const loadCertificate = async (audit) => {
    try {
      const r = await axiosInstance.get(`/partner/context/clients/${org.id}/audits/${audit.id}/trust-certificate`);
      setCertificate({ audit, data: r.data });
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.error'),
        text: e?.response?.data?.message || t('CpaClientWorkspace.failedToLoadCertificate') });
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700 }}>{t('CpaClientWorkspace.auditHistory')}</h4>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.87rem' }}>
            {t('CpaClientWorkspace.auditHistorySubtitle', { name: org.name })}
          </p>
        </div>
        {canCreateAudit && (
          <button className="au-create-btn" onClick={openCreate}><i className="fas fa-plus" /> {t('CpaClientWorkspace.newAudit')}</button>
        )}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /></div>
        : audits.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <i className="fas fa-clipboard" style={{ fontSize: 36, marginBottom: 16, display: 'block' }} />
            <p style={{ fontWeight: 600, fontSize: '1rem', color: '#64748b' }}>{t('CpaClientWorkspace.noAuditsYet')}</p>
            <p style={{ fontSize: '0.87rem' }}>{t('CpaClientWorkspace.createFirstAudit')}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="au-table">
              <thead>
                <tr>
                  <th>{t('CpaClientWorkspace.colAuditId')}</th><th>{t('CpaClientWorkspace.colType')}</th><th>{t('CpaClientWorkspace.colCreated')}</th>
                  <th>{t('CpaClientWorkspace.colControls')}</th><th>{t('CpaClientWorkspace.colPass')}</th><th>{t('CpaClientWorkspace.colFail')}</th><th>{t('CpaClientWorkspace.colScore')}</th><th>{t('CpaClientWorkspace.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {audits.map(a => (
                  <tr key={a.id}>
                    <td><span style={{ fontFamily: 'monospace', fontSize: '0.83rem' }}>{a.auditId || '—'}</span></td>
                    <td>
                      {a.auditType ? (
                        <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px',
                          borderRadius: 10, fontSize: '0.72rem', fontWeight: 700 }}>
                          {a.auditType.replace('SOC2_', 'SOC2 ').replace('_', ' ')}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{fmt(a.createdDate)}</td>
                    <td>{a.totalControls ?? '—'}</td>
                    <td style={{ color: '#10b981', fontWeight: 600 }}>{a.compliantCount ?? '—'}</td>
                    <td style={{ color: '#ef4444', fontWeight: 600 }}>{a.failingCount ?? '—'}</td>
                    <td>
                      <span style={{ fontWeight: 700,
                        color: (a.pct ?? 0) >= 95 ? '#10b981' : (a.pct ?? 0) >= 50 ? '#f59e0b' : '#ef4444' }}>
                        {a.pct ?? 0}%
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleRun(a)} disabled={runningId === a.id}
                        style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6,
                          padding: '4px 10px', fontSize: '0.78rem', fontWeight: 600,
                          cursor: runningId === a.id ? 'wait' : 'pointer', marginRight: 6 }}>
                        {runningId === a.id ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.running')}</> : <><i className="fas fa-play" /> {t('CpaClientWorkspace.run')}</>}
                      </button>
                      <button onClick={() => loadCertificate(a)}
                        style={{ background: (a.pct ?? 0) >= 95 && (a.failingCount === 0) ? '#10b981' : '#f59e0b',
                          color: '#fff', border: 'none', borderRadius: 6,
                          padding: '4px 10px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                        <i className={(a.pct ?? 0) >= 95 && (a.failingCount === 0) ? 'fas fa-certificate' : 'fas fa-clipboard-list'} />
                        {' '}{(a.pct ?? 0) >= 95 && (a.failingCount === 0) ? t('CpaClientWorkspace.cert') : t('CpaClientWorkspace.gaps')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {showCreate && (
        <div className="au-overlay" onClick={() => setShowCreate(false)}>
          <div className="au-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="au-modal-header">
              <span><i className="fas fa-plus" /> {t('CpaClientWorkspace.newAuditFor', { name: org.name })}</span>
              <button className="au-modal-close" onClick={() => setShowCreate(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="au-modal-body">
              <div className="au-field">
                <label>{t('CpaClientWorkspace.frameworkRequired')}</label>
                <select className="au-input" value={frameworkId} onChange={e => setFrameworkId(e.target.value)}>
                  <option value="">{t('CpaClientWorkspace.selectFramework')}</option>
                  {frameworks.map(f => <option key={f.id} value={f.id}>{f.displayName}</option>)}
                </select>
                {frameworks.length === 0 && <p className="au-field-hint">{t('CpaClientWorkspace.noFrameworksEnrolledHint')}</p>}
              </div>

              {/* SOC 2 Type 1 / Type 2 picker + date range */}
              {isSoc2 && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
                  padding: 12, marginBottom: 12 }}>
                  <div style={{ fontSize: '0.78rem', color: '#075985', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('CpaClientWorkspace.soc2AuditType')}</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    {[
                      { v: 'SOC2_TYPE_1', label: t('CpaClientWorkspace.type1'), hint: t('CpaClientWorkspace.pointInTime') },
                      { v: 'SOC2_TYPE_2', label: t('CpaClientWorkspace.type2'), hint: t('CpaClientWorkspace.periodCoverage') },
                    ].map(opt => (
                      <button key={opt.v} type="button" onClick={() => setAuditType(opt.v)}
                        style={{ flex: 1, padding: '8px 10px',
                          border: auditType === opt.v ? '2px solid #0ea5e9' : '2px solid #e2e8f0',
                          background: auditType === opt.v ? '#e0f2fe' : '#fff', borderRadius: 8,
                          cursor: 'pointer', textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, color: '#0c4a6e', fontSize: '0.85rem' }}>
                          <i className={`far ${auditType === opt.v ? 'fa-dot-circle' : 'fa-circle'}`} style={{ marginRight: 4 }} />
                          {opt.label}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: 2 }}>{opt.hint}</div>
                      </button>
                    ))}
                  </div>
                  {auditType === 'SOC2_TYPE_1' && (
                    <div className="au-field" style={{ marginBottom: 0 }}>
                      <label>{t('CpaClientWorkspace.asOfDateRequired')}</label>
                      <input type="date" className="au-input"
                        value={asOfDate} onChange={e => setAsOfDate(e.target.value)} />
                    </div>
                  )}
                  {auditType === 'SOC2_TYPE_2' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div className="au-field" style={{ marginBottom: 0 }}>
                        <label>{t('CpaClientWorkspace.periodStartRequired')}</label>
                        <input type="date" className="au-input"
                          value={periodStartDate} onChange={e => setPeriodStartDate(e.target.value)} />
                      </div>
                      <div className="au-field" style={{ marginBottom: 0 }}>
                        <label>{t('CpaClientWorkspace.periodEndRequired')}</label>
                        <input type="date" className="au-input"
                          value={periodEndDate} onChange={e => setPeriodEndDate(e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="au-field">
                <label>{t('CpaClientWorkspace.auditorRequired')}</label>
                <div className="au-auditor-toggle" style={{ marginBottom: 10 }}>
                  <button type="button" className={`au-toggle-btn ${auditorMode === 'internal' ? 'active' : ''}`}
                    onClick={() => { setAuditorMode('internal'); setExtAuditor(''); }}>
                    <i className="fas fa-users" /> {t('CpaClientWorkspace.fromMyTeam')}
                  </button>
                  <button type="button" className={`au-toggle-btn ${auditorMode === 'external' ? 'active' : ''}`}
                    onClick={() => { setAuditorMode('external'); setAuditorId(''); }}>
                    <i className="fas fa-user-plus" /> {t('CpaClientWorkspace.external')}
                  </button>
                </div>
                {auditorMode === 'internal' ? (
                  <select className="au-input" value={auditorId} onChange={e => setAuditorId(e.target.value)}>
                    <option value="">{t('CpaClientWorkspace.selectFromYourTeam')}</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.org})</option>)}
                  </select>
                ) : (
                  <input className="au-input" placeholder={t('CpaClientWorkspace.externalAuditorName')}
                    value={extAuditor} onChange={e => setExtAuditor(e.target.value)} />
                )}
              </div>

              {/* Optional scheduled run */}
              <div className="au-field">
                <label>{t('CpaClientWorkspace.scheduleFirstRun')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('CpaClientWorkspace.optional')}</span></label>
                <input type="datetime-local" className="au-input"
                  value={scheduledRunAt} onChange={e => setScheduledRunAt(e.target.value)} />
                <p className="au-field-hint">{t('CpaClientWorkspace.leaveBlankRunNow')}</p>
              </div>

              {/* Vendor scope preview */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: 10, marginTop: 4 }}>
                <div style={{ fontSize: '0.74rem', color: '#475569', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  {t('CpaClientWorkspace.vendorsInScope')}
                </div>
                {vendors.length === 0 ? (
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    {t('CpaClientWorkspace.noVendorsInScopeHint')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {vendors.map(v => (
                      <span key={v} style={{ background: '#dbeafe', color: '#1e40af',
                        padding: '3px 10px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 700 }}>
                        <i className="fas fa-plug" style={{ marginRight: 4 }} />{v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setShowCreate(false)}>{t('CpaClientWorkspace.cancel')}</button>
              <button className="mp-btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.creating')}</> : <><i className="fas fa-plus" /> {t('CpaClientWorkspace.createAudit')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Trust Certificate / Gap Report modal ── */}
      {certificate && (
        <div className="au-overlay" onClick={() => setCertificate(null)}>
          <div className="au-modal" style={{ maxWidth: 720, maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div className="au-modal-header">
              <span><i className={certificate.data.status === 'ISSUED' ? 'fas fa-certificate' : 'fas fa-clipboard-list'} />
                {' '}{certificate.data.status === 'ISSUED' ? t('CpaClientWorkspace.trustCertificate') : t('CpaClientWorkspace.auditGapReport')}
              </span>
              <button className="au-modal-close" onClick={() => setCertificate(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="au-modal-body">
              {certificate.data.status === 'ISSUED' ? (
                <>
                  {/* Issued certificate panel */}
                  <div style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '2px solid #10b981', borderRadius: 14, padding: '24px 28px', marginBottom: 18,
                    textAlign: 'center' }}>
                    <i className="fas fa-certificate" style={{ fontSize: 48, color: '#10b981', marginBottom: 10 }} />
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#065f46' }}>
                      {t('CpaClientWorkspace.certificateOfCompliance')}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#047857', textTransform: 'uppercase',
                      letterSpacing: 2, fontWeight: 700, marginTop: 4 }}>
                      {certificate.data.auditType ? certificate.data.auditType.replace('_', ' ') : 'SOC 2'}
                    </div>
                    <div style={{ marginTop: 18, fontSize: '0.92rem', color: '#064e3b', lineHeight: 1.55 }}>
                      {certificate.data.attestation}
                    </div>
                    <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid #6ee7b7',
                      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: '0.83rem' }}>
                      <div><strong>{t('CpaClientWorkspace.issuedBy')}</strong> {certificate.data.issuedBy}</div>
                      <div><strong>{t('CpaClientWorkspace.certificateId')}</strong> <span style={{ fontFamily: 'monospace' }}>{certificate.data.certificateId}</span></div>
                      <div><strong>{t('CpaClientWorkspace.issuedAt')}</strong> {fmt(certificate.data.issuedAt)}</div>
                      <div><strong>{t('CpaClientWorkspace.passRateLabel')}</strong> {certificate.data.passRate}%</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Gap report panel */}
                  <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 12,
                    padding: '14px 18px', marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                      <i className="fas fa-triangle-exclamation" /> {t('CpaClientWorkspace.certCannotBeIssued')}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#78350f' }}>
                      {certificate.data.reason}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
                    {[
                      { label: t('CpaClientWorkspace.totalControls'), v: certificate.data.totalControls,   color: '#475569' },
                      { label: t('CpaClientWorkspace.passing'),        v: certificate.data.passedControls,  color: '#10b981' },
                      { label: t('CpaClientWorkspace.failing'),        v: certificate.data.failedControls,  color: '#ef4444' },
                      { label: t('CpaClientWorkspace.passRateLabelShort'),      v: certificate.data.passRate + '%',  color: '#3b82f6' },
                    ].map(s => (
                      <div key={s.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0',
                        borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ fontWeight: 800, fontSize: '1.25rem', color: s.color }}>{s.v}</div>
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Gap list grouped by severity */}
                  {(certificate.data.gaps || []).length > 0 && (
                    <>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>
                        {t('CpaClientWorkspace.openGaps', { count: certificate.data.gaps.length })}
                      </div>
                      <table className="au-table" style={{ fontSize: '0.82rem' }}>
                        <thead>
                          <tr><th>{t('CpaClientWorkspace.colSeverity')}</th><th>{t('CpaClientWorkspace.colCategory')}</th><th>{t('CpaClientWorkspace.colControl')}</th><th>{t('CpaClientWorkspace.colTest')}</th></tr>
                        </thead>
                        <tbody>
                          {certificate.data.gaps.map((g, i) => {
                            const sev = (g.severity || 'MEDIUM').toUpperCase();
                            const sevColor = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#f59e0b', LOW: '#3b82f6' }[sev] || '#94a3b8';
                            return (
                              <tr key={i}>
                                <td>
                                  <span style={{ background: sevColor + '22', color: sevColor,
                                    padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 800 }}>
                                    {sev}
                                  </span>
                                </td>
                                <td>{g.categoryShorthand} — {g.category}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{g.control}</td>
                                <td>{g.test}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setCertificate(null)}>{t('CpaClientWorkspace.close')}</button>
              {certificate.data.status !== 'ISSUED' && (
                <button
                  onClick={async () => {
                    const r = await Swal.fire({
                      title: t('CpaClientWorkspace.autoRemediateTitle'),
                      html: t('CpaClientWorkspace.autoRemediateHtml'),
                      icon: 'question', showCancelButton: true,
                      confirmButtonText: t('CpaClientWorkspace.adoptPolicies'), confirmButtonColor: '#3b82f6',
                    });
                    if (!r.isConfirmed) return;
                    try {
                      const resp = await axiosInstance.post(
                        `/partner/context/clients/${org.id}/audits/${certificate.audit.id}/adopt-policies`);
                      const list = (resp.data?.adoptedPolicies || []).join(', ') || t('CpaClientWorkspace.none');
                      const after = await Swal.fire({
                        icon: resp.data?.policiesAdopted > 0 ? 'success' : 'info',
                        title: resp.data?.message || t('CpaClientWorkspace.done'),
                        html: t('CpaClientWorkspace.adoptedHtml', { list }),
                        showCancelButton: true, confirmButtonText: t('CpaClientWorkspace.rerunAuditNow'),
                        cancelButtonText: t('CpaClientWorkspace.later'),
                      });
                      if (after.isConfirmed) {
                        setCertificate(null);
                        handleRun(certificate.audit);
                      }
                    } catch (e) {
                      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.failed'),
                        text: e?.response?.data?.message || t('CpaClientWorkspace.autoRemediationFailed') });
                    }
                  }}
                  style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8,
                    padding: '8px 16px', fontWeight: 700, cursor: 'pointer' }}>
                  <i className="fas fa-magic-wand-sparkles" /> {t('CpaClientWorkspace.autoRemediatePolicyGaps')}
                </button>
              )}
              <button className="mp-btn-primary" onClick={() => window.print()}>
                <i className="fas fa-print" /> {t('CpaClientWorkspace.printSavePdf')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Policies tab ──────────────────────────────────────────────────────────────

function PoliciesTab({ org }) {
  const { t } = useTranslation();
  const [policies,  setPolicies]  = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [busyId,    setBusyId]    = useState(null);
  const [view,      setView]      = useState('adopted');

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get(`/partner/context/clients/${org.id}/policies`),
      axiosInstance.get('/policies/templates'),
    ]).then(([pRes, tRes]) => {
      if (pRes.status === 'fulfilled') setPolicies(pRes.value.data || []);
      if (tRes.status === 'fulfilled') setTemplates(tRes.value.data || []);
    }).finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const adoptedNames = new Set(policies.map(p => (p.name || '').toLowerCase().trim()));
  // Template DTO uses policyId/policyName field names (see PolicyResponseDTO);
  // normalise so the rest of the component can rely on .id and .name.
  const normalisedTemplates = templates.map(t => ({
    id:          t.policyId ?? t.id,
    name:        t.policyName ?? t.name,
    description: t.description,
  }));
  const missingTemplates = normalisedTemplates.filter(t =>
    !adoptedNames.has((t.name || '').toLowerCase().trim()));

  const publish = async (orgPolicyId) => {
    setBusyId(orgPolicyId);
    try {
      await axiosInstance.post(`/partner/context/clients/${org.id}/policies/${orgPolicyId}/publish`);
      load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.publishFailed'),
        text: e?.response?.data?.error || e?.message });
    } finally { setBusyId(null); }
  };

  const inherit = async (templateId) => {
    setBusyId('t' + templateId);
    try {
      await axiosInstance.post(`/partner/context/clients/${org.id}/policies/inherit/${templateId}`);
      load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.inheritFailed'),
        text: e?.response?.data?.error || e?.message });
    } finally { setBusyId(null); }
  };

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h4 style={{ margin: 0, fontWeight: 700 }}>{t('CpaClientWorkspace.policies')}</h4>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.87rem' }}>
          {t('CpaClientWorkspace.policiesSubtitle', { name: org.name })}
        </p>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 16 }}>
        {[
          { k: 'adopted', label: t('CpaClientWorkspace.adoptedPolicies', { count: policies.length }) },
          { k: 'tpl',     label: t('CpaClientWorkspace.templateLibraryAvailable', { count: missingTemplates.length }) },
        ].map(tab => (
          <button key={tab.k} onClick={() => setView(tab.k)}
            style={{ padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: view === tab.k ? 700 : 500, fontSize: '0.88rem',
              color: view === tab.k ? '#3b82f6' : '#64748b',
              borderBottom: view === tab.k ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: -1 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /></div>
        : view === 'adopted' ? (
          policies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <i className="fas fa-file-shield" style={{ fontSize: 36, marginBottom: 16, display: 'block' }} />
              <p style={{ fontWeight: 600, color: '#64748b' }}>{t('CpaClientWorkspace.noPoliciesAdopted')}</p>
              <p style={{ fontSize: '0.87rem' }}>{t('CpaClientWorkspace.switchToTemplateLibrary')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {policies.map(p => {
                const status = (p.policy_status || 'DRAFT').toUpperCase();
                const s = POLICY_STATUS_STYLE[status] || POLICY_STATUS_STYLE.DRAFT;
                const isPublished = status === 'PUBLISHED';
                return (
                  <div key={p.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>{p.name}</div>
                        {p.description && <div style={{ fontSize: '0.83rem', color: '#64748b', marginTop: 4 }}>{p.description}</div>}
                        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 6 }}>
                          {t('CpaClientWorkspace.versionEffective', { version: p.version || '—', date: fmt(p.effective_date) })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 800, padding: '3px 10px',
                          borderRadius: 20, background: s.bg, color: s.color }}>
                          {status || 'DRAFT'}
                        </span>
                        {!isPublished && (
                          <button onClick={() => publish(p.id)} disabled={busyId === p.id}
                            style={{ padding: '4px 12px', background: '#10b981', color: '#fff',
                              border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              cursor: busyId === p.id ? 'wait' : 'pointer' }}>
                            {busyId === p.id ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.publishing')}</>
                              : <><i className="fas fa-check" style={{ marginRight: 4 }} /> {t('CpaClientWorkspace.publish')}</>}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          missingTemplates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <i className="fas fa-check-double" style={{ fontSize: 36, marginBottom: 16,
                display: 'block', color: '#10b981' }} />
              <p style={{ fontWeight: 600, color: '#64748b' }}>{t('CpaClientWorkspace.allTemplatesAdopted')}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 12 }}>
              {missingTemplates.map(tpl => {
                const busy = busyId === 't' + tpl.id;
                return (
                  <div key={tpl.id} style={{ background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{tpl.name}</div>
                    {tpl.description && <div style={{ fontSize: '0.78rem', color: '#64748b',
                      marginTop: 6, lineHeight: 1.4 }}>{tpl.description.slice(0,140)}{tpl.description.length>140?'…':''}</div>}
                    <button onClick={() => inherit(tpl.id)} disabled={busy}
                      style={{ marginTop: 10, padding: '6px 12px', background: '#3b82f6', color: '#fff',
                        border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        cursor: busy ? 'wait' : 'pointer' }}>
                      {busy ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.inheriting')}</>
                        : <><i className="fas fa-plus" style={{ marginRight: 4 }} /> {t('CpaClientWorkspace.inherit')}</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
    </div>
  );
}

// ── Documents tab ─────────────────────────────────────────────────────────────

function DocumentsTab({ org }) {
  const { t } = useTranslation();
  const [orgDocs,   setOrgDocs]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [docTab,    setDocTab]    = useState('my');
  const [search,    setSearch]    = useState('');
  const [busyId,    setBusyId]    = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get(`/partner/context/clients/${org.id}/org-documents`),
      axiosInstance.get('/partner/context/template-documents'),
    ]).then(([orgRes, tplRes]) => {
      if (orgRes.status === 'fulfilled')  setOrgDocs(orgRes.value.data || []);
      if (tplRes.status === 'fulfilled')  setTemplates(tplRes.value.data || []);
    }).finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const inheritedTemplateIds = new Set(orgDocs.map(d => d.source_template_id).filter(Boolean));
  const inheritDoc = async (templateId) => {
    setBusyId(templateId);
    try {
      await axiosInstance.post(`/partner/context/clients/${org.id}/documents/inherit/${templateId}`);
      load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.inheritFailed'),
        text: e?.response?.data?.error || e?.message });
    } finally { setBusyId(null); }
  };

  const filteredTemplates = templates.filter(t =>
    !search || t.title?.toLowerCase().includes(search.toLowerCase()) || t.category?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 20,
        borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
        {[{ key: 'my', label: t('CpaClientWorkspace.orgDocuments', { count: orgDocs.length }) }, { key: 'tpl', label: t('CpaClientWorkspace.templateLibrary', { count: templates.length }) }].map(tab => (
          <button key={tab.key} onClick={() => setDocTab(tab.key)}
            style={{ padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: docTab === tab.key ? 700 : 400, color: docTab === tab.key ? '#3b82f6' : '#64748b',
              borderBottom: docTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
              fontSize: '0.9rem', marginBottom: -1 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /></div>
        : docTab === 'my' ? (
          orgDocs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <i className="fas fa-folder-open" style={{ fontSize: 36, marginBottom: 16, display: 'block' }} />
              <p style={{ fontWeight: 600, color: '#64748b' }}>{t('CpaClientWorkspace.noDocumentsYet')}</p>
              <p style={{ fontSize: '0.87rem' }}>{t('CpaClientWorkspace.inheritTemplateToStart')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {orgDocs.map(d => {
                const s = DOC_STATUS_STYLE[d.status] || DOC_STATUS_STYLE.DRAFT;
                return (
                  <div key={d.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px',
                    display: 'flex', alignItems: 'center', gap: 14 }}>
                    <i className="fas fa-file-alt" style={{ fontSize: 24, color: '#3b82f6' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.93rem' }}>{d.title}</div>
                      {d.template_title && <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{t('CpaClientWorkspace.templateLabel', { title: d.template_title })}</div>}
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{fmt(d.created_at)}</div>
                    </div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px',
                      borderRadius: 20, background: s.bg, color: s.color }}>
                      {d.status || 'DRAFT'}
                    </span>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <>
            <input className="mp-input" placeholder={t('CpaClientWorkspace.searchTemplates')} value={search}
              onChange={e => setSearch(e.target.value)} style={{ marginBottom: 14, maxWidth: 360 }} />
            {filteredTemplates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                <p>{t('CpaClientWorkspace.noTemplatesMatch')}</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {filteredTemplates.map(tpl => {
                  const inherited = inheritedTemplateIds.has(tpl.id);
                  const busy = busyId === tpl.id;
                  return (
                    <div key={tpl.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <i className="fas fa-file-contract" style={{ fontSize: 20, color: '#3b82f6', marginTop: 2 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{tpl.title}</div>
                          <span style={{ fontSize: '0.73rem', background: '#dbeafe', color: '#1e40af',
                            borderRadius: 10, padding: '2px 8px', display: 'inline-block', marginTop: 4 }}>
                            {tpl.category || t('CpaClientWorkspace.general')}
                          </span>
                          {tpl.description && <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 6 }}>{tpl.description}</div>}
                          <button onClick={() => inheritDoc(tpl.id)} disabled={inherited || busy}
                            style={{ marginTop: 10, padding: '5px 12px',
                              background: inherited ? '#e5e7eb' : '#3b82f6',
                              color: inherited ? '#6b7280' : '#fff',
                              border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              cursor: inherited ? 'default' : (busy ? 'wait' : 'pointer') }}>
                            {inherited ? <><i className="fas fa-check" style={{ marginRight: 4 }} /> {t('CpaClientWorkspace.inherited')}</>
                              : busy ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.inheriting')}</>
                              : <><i className="fas fa-plus" style={{ marginRight: 4 }} /> {t('CpaClientWorkspace.inherit')}</>}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
    </div>
  );
}

// ── ASM tab ──────────────────────────────────────────────────────────────────

function AsmTab({ org }) {
  const { t } = useTranslation();
  const [domains, setDomains] = useState([]);
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState({}); // { [domainId]: true }
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain,     setNewDomain]     = useState('');
  const [saving,        setSaving]        = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get(`/partner/context/clients/${org.id}/asm/domains?page=0&size=50`),
      axiosInstance.get(`/partner/context/clients/${org.id}/asm/stats`),
    ]).then(([dRes, sRes]) => {
      if (dRes.status === 'fulfilled') setDomains(dRes.value.data?.content || dRes.value.data?.domains || []);
      if (sRes.status === 'fulfilled') setStats(sRes.value.data || {});
    }).finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    setSaving(true);
    try {
      await axiosInstance.post(`/partner/context/clients/${org.id}/asm/domains`, { name: newDomain.trim() });
      setNewDomain(''); setShowAddDomain(false); load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.error'), text: e?.response?.data?.message || t('CpaClientWorkspace.failedToAddDomain') });
    } finally { setSaving(false); }
  };

  const handleScan = async (domainId, mode) => {
    setScanning(s => ({ ...s, [domainId]: true }));
    try {
      await axiosInstance.post(`/partner/context/clients/${org.id}/asm/domains/${domainId}/scan`, { mode });
      Swal.fire({ icon: 'success', title: t('CpaClientWorkspace.scanStarted', { mode }), timer: 1500, showConfirmButton: false });
      setTimeout(load, 2000);
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.error'), text: t('CpaClientWorkspace.failedToStartScan') });
    } finally {
      setTimeout(() => setScanning(s => { const n = { ...s }; delete n[domainId]; return n; }), 3000);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700 }}>{t('CpaClientWorkspace.externalAttackSurface')}</h4>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.87rem' }}>
            {t('CpaClientWorkspace.externalAttackSurfaceSubtitle', { name: org.name })}
          </p>
        </div>
        <button className="au-create-btn" onClick={() => setShowAddDomain(true)}>
          <i className="fas fa-plus" /> {t('CpaClientWorkspace.addDomain')}
        </button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
          {[
            { label: t('CpaClientWorkspace.domains'),   value: stats.totalDomains    ?? domains.length, icon: 'fa-globe',       color: '#3b82f6' },
            { label: t('CpaClientWorkspace.subdomains'), value: stats.totalSubdomains ?? '—', icon: 'fa-sitemap',     color: '#0ea5e9' },
            { label: t('CpaClientWorkspace.openPorts'), value: stats.totalPorts      ?? '—', icon: 'fa-network-wired', color: '#f59e0b' },
            { label: t('CpaClientWorkspace.findings'),   value: stats.totalFindings   ?? '—', icon: 'fa-triangle-exclamation', color: '#ef4444' },
          ].map(c => (
            <div key={c.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <i className={`fas ${c.icon}`} style={{ fontSize: 18, color: c.color }} />
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1.2rem' }}>{c.value}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{c.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /></div>
        : domains.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <i className="fas fa-globe" style={{ fontSize: 36, marginBottom: 16, display: 'block' }} />
            <p style={{ fontWeight: 600, color: '#64748b' }}>{t('CpaClientWorkspace.noDomainsAdded')}</p>
            <p style={{ fontSize: '0.87rem' }}>{t('CpaClientWorkspace.addDomainToStart')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {domains.map(d => (
              <div key={d.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px',
                display: 'flex', alignItems: 'center', gap: 12 }}>
                <i className="fas fa-globe" style={{ fontSize: 22, color: '#3b82f6' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{d.name}</div>
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    {t('CpaClientWorkspace.subdomainsFindings', { subdomains: (d.subdomainCount ?? d.totalSubdomains ?? 0), findings: (d.findingCount ?? d.totalFindings ?? 0) })}
                    {d.lastScanStatus && ` · ${d.lastScanStatus}`}
                  </div>
                </div>
                <button onClick={() => handleScan(d.id, 'INCREMENTAL')} disabled={scanning[d.id]}
                  style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8,
                    padding: '7px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: scanning[d.id] ? 'wait' : 'pointer' }}>
                  {scanning[d.id] ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.starting')}</> : <><i className="fas fa-play" /> {t('CpaClientWorkspace.continue')}</>}
                </button>
                <button onClick={() => handleScan(d.id, 'FULL')} disabled={scanning[d.id]}
                  style={{ background: '#15803d', color: '#fff', border: 'none', borderRadius: 8,
                    padding: '7px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: scanning[d.id] ? 'wait' : 'pointer' }}>
                  <i className="fas fa-sync" /> {t('CpaClientWorkspace.full')}
                </button>
              </div>
            ))}
          </div>
        )}

      {showAddDomain && (
        <div className="au-overlay" onClick={() => setShowAddDomain(false)}>
          <div className="au-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="au-modal-header">
              <span><i className="fas fa-plus" /> {t('CpaClientWorkspace.addDomain')}</span>
              <button className="au-modal-close" onClick={() => setShowAddDomain(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="au-modal-body">
              <div className="au-field">
                <label>{t('CpaClientWorkspace.domainRequired')}</label>
                <input className="au-input" placeholder="example.com"
                  value={newDomain} onChange={e => setNewDomain(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddDomain()} autoFocus />
                <p className="au-field-hint">{t('CpaClientWorkspace.domainHint')}</p>
              </div>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setShowAddDomain(false)}>{t('CpaClientWorkspace.cancel')}</button>
              <button className="mp-btn-primary" onClick={handleAddDomain} disabled={saving || !newDomain.trim()}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.adding')}</> : <><i className="fas fa-plus" /> {t('CpaClientWorkspace.addDomain')}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── iASM tab — embed InternalAsmDashboard scoped to client org ──────────────

function IasmTab({ org }) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [Dashboard, setDashboard] = useState(null);

  useEffect(() => {
    import('../Admin/iasm/InternalAsmDashboard').then(mod => {
      setDashboard(() => mod.default);
      setLoaded(true);
    });
  }, []);

  if (!loaded) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
    <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /> {t('CpaClientWorkspace.loadingIasm')}
  </div>;

  return <div style={{ margin: '-24px -28px', padding: 0 }}>
    {Dashboard && <Dashboard forcedOrg={{ id: org.id, name: org.name }} />}
  </div>;
}

// ── Team tab ─────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'ORG_ADMIN', label: i18n.t('CpaClientWorkspace.roleOrgAdmin') },
  { value: 'MEMBER',    label: i18n.t('CpaClientWorkspace.roleMember') },
  { value: 'AUDITOR',   label: i18n.t('CpaClientWorkspace.roleAuditor') },
  { value: 'VIEWER',    label: i18n.t('CpaClientWorkspace.roleViewer') },
];

function TeamTab({ org }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '', orgRole: 'MEMBER', message: '' });
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance.get(`/partner/context/clients/${org.id}/team`)
      .then(r => setMembers(r.data || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    if (!inviteForm.email.trim()) { Swal.fire({ icon: 'warning', title: t('CpaClientWorkspace.emailRequired') }); return; }
    setSaving(true);
    try {
      const r = await axiosInstance.post(`/partner/context/clients/${org.id}/team/invite`, inviteForm);
      setInviteResult({
        ...r.data,
        link: `${window.location.origin}/#/accept-invite?token=${r.data.token}`,
      });
      setInviteForm({ email: '', firstName: '', lastName: '', orgRole: 'MEMBER', message: '' });
      load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.error'), text: e?.response?.data?.error || t('CpaClientWorkspace.failedToSendInvite') });
    } finally { setSaving(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axiosInstance.put(`/partner/context/clients/${org.id}/team/${userId}/role`, { orgRole: newRole });
      load();
    } catch { Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.error'), text: t('CpaClientWorkspace.failedToUpdateRole') }); }
  };

  const handleRemove = async (userId) => {
    const r = await Swal.fire({ title: t('CpaClientWorkspace.removeMemberTitle'), icon: 'warning', showCancelButton: true,
      confirmButtonText: t('CpaClientWorkspace.remove'), confirmButtonColor: '#ef4444' });
    if (!r.isConfirmed) return;
    try {
      await axiosInstance.delete(`/partner/context/clients/${org.id}/team/${userId}`);
      load();
    } catch { Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.error'), text: t('CpaClientWorkspace.failedToRemoveMember') }); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    Swal.fire({ icon: 'success', title: t('CpaClientWorkspace.linkCopied'), timer: 1200, showConfirmButton: false });
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700 }}>{t('CpaClientWorkspace.teamAndAccess')}</h4>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.87rem' }}>
            {t('CpaClientWorkspace.teamAndAccessSubtitle', { name: org.name })}
          </p>
        </div>
        <button className="au-create-btn" onClick={() => { setShowInvite(true); setInviteResult(null); }}>
          <i className="fas fa-user-plus" /> {t('CpaClientWorkspace.inviteMember')}
        </button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /></div>
        : members.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <i className="fas fa-users" style={{ fontSize: 36, marginBottom: 16, display: 'block' }} />
            <p style={{ fontWeight: 600, color: '#64748b' }}>{t('CpaClientWorkspace.noTeamMembers')}</p>
            <p style={{ fontSize: '0.87rem' }}>{t('CpaClientWorkspace.inviteStaffHint')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {members.map(m => (
              <div key={m.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 18,
                  background: m.pending ? '#fef3c7' : '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, color: m.pending ? '#92400e' : '#1e40af' }}>
                  {(m.firstName?.[0] || m.email?.[0] || '?').toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {m.firstName || ''} {m.lastName || ''}
                    {m.pending && <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px',
                      borderRadius: 10, background: '#fef3c7', color: '#92400e' }}>{t('CpaClientWorkspace.pending')}</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{m.email}</div>
                </div>
                {m.pending ? (
                  <button onClick={() => copyLink(`${window.location.origin}/#/accept-invite?token=${m.token}`)}
                    style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 12px',
                      cursor: 'pointer', color: '#475569', fontSize: 13, fontWeight: 600 }}>
                    <i className="fas fa-link" /> {t('CpaClientWorkspace.copyLink')}
                  </button>
                ) : (
                  <>
                    <select value={m.orgRole || 'MEMBER'} onChange={e => handleRoleChange(m.id, e.target.value)}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}>
                      {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <button onClick={() => handleRemove(m.id)}
                      style={{ background: '#fee2e2', border: 'none', borderRadius: 8, padding: '6px 10px',
                        cursor: 'pointer', color: '#ef4444', fontSize: 13 }} title={t('CpaClientWorkspace.remove')}>
                      <i className="fas fa-user-slash" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

      {showInvite && (
        <div className="au-overlay" onClick={() => setShowInvite(false)}>
          <div className="au-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="au-modal-header">
              <span><i className="fas fa-user-plus" /> {t('CpaClientWorkspace.inviteTeamMember')}</span>
              <button className="au-modal-close" onClick={() => setShowInvite(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="au-modal-body">
              {inviteResult ? (
                <>
                  <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, color: '#14532d', marginBottom: 6 }}>
                      <i className="fas fa-check-circle" /> {t('CpaClientWorkspace.invitationSent')}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#166534' }}>
                      {t('CpaClientWorkspace.expires', { date: new Date(inviteResult.expiresAt).toLocaleDateString() })}
                    </div>
                  </div>
                  <div className="au-field">
                    <label>{t('CpaClientWorkspace.inviteLink')}</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="au-input" readOnly value={inviteResult.link}
                        style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.78rem' }} />
                      <button className="mp-btn-primary" onClick={() => copyLink(inviteResult.link)}>
                        <i className="fas fa-copy" /> {t('CpaClientWorkspace.copy')}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="au-field"><label>{t('CpaClientWorkspace.emailRequiredLabel')}</label>
                    <input className="au-input" type="email" placeholder="user@example.com"
                      value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} /></div>
                  <div className="au-field" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div><label>{t('CpaClientWorkspace.firstName')}</label>
                      <input className="au-input" value={inviteForm.firstName}
                        onChange={e => setInviteForm(f => ({ ...f, firstName: e.target.value }))} /></div>
                    <div><label>{t('CpaClientWorkspace.lastName')}</label>
                      <input className="au-input" value={inviteForm.lastName}
                        onChange={e => setInviteForm(f => ({ ...f, lastName: e.target.value }))} /></div>
                  </div>
                  <div className="au-field"><label>{t('CpaClientWorkspace.role')}</label>
                    <select className="au-input" value={inviteForm.orgRole}
                      onChange={e => setInviteForm(f => ({ ...f, orgRole: e.target.value }))}>
                      {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select></div>
                  <div className="au-field"><label>{t('CpaClientWorkspace.message')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('CpaClientWorkspace.optional')}</span></label>
                    <textarea className="au-input" rows={2}
                      value={inviteForm.message} onChange={e => setInviteForm(f => ({ ...f, message: e.target.value }))} /></div>
                </>
              )}
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setShowInvite(false)}>
                {inviteResult ? t('CpaClientWorkspace.done') : t('CpaClientWorkspace.cancel')}
              </button>
              {!inviteResult && (
                <button className="mp-btn-primary" onClick={handleInvite} disabled={saving || !inviteForm.email.trim()}>
                  {saving ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.sending')}</> : <><i className="fas fa-paper-plane" /> {t('CpaClientWorkspace.sendInvite')}</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Frameworks tab ──────────────────────────────────────────────────────────

// Fallback only; the canonical partner price + framework list come from /api/partner/billing/pricing.
const DEFAULT_PARTNER_PRICE = 149;

function FrameworksTab({ org }) {
  const { t } = useTranslation();
  const [enrolled, setEnrolled] = useState([]);
  const [pricing,  setPricing]  = useState([]);   // [{ frameworkId, frameworkName, monthlyPrice }] — single source
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [newFw,    setNewFw]    = useState('');
  const [newFee,   setNewFee]   = useState('');
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosInstance.get(`/partner/cpa/orgs/${org.id}/frameworks`),
      axiosInstance.get('/partner/billing/pricing'),
    ])
      .then(([fwRes, priceRes]) => { setEnrolled(fwRes.data || []); setPricing(priceRes.data || []); })
      .catch(() => { setEnrolled([]); })
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const priceByName = Object.fromEntries((pricing || []).map(p => [p.frameworkName, Number(p.monthlyPrice)]));
  const partnerPrice = pricing.length ? Number(pricing[0].monthlyPrice) : DEFAULT_PARTNER_PRICE;
  const priceFor = (name) => priceByName[name] ?? partnerPrice;
  const enrolledNames = enrolled.map(f => f.display_name || f.name);
  // Dropdown is sourced from live framework pricing (display_name), so new frameworks appear automatically.
  const available = pricing.map(p => p.frameworkName).filter(fw => !enrolledNames.includes(fw));

  const handleAdd = async () => {
    if (!newFw) return;
    setSaving(true);
    try {
      await axiosInstance.post(`/partner/cpa/orgs/${org.id}/frameworks`, {
        frameworkName: newFw, auditFee: parseFloat(newFee || '0') || 0,
      });
      setNewFw(''); setNewFee(''); setShowAdd(false); load();
    } catch (e) {
      Swal.fire({ icon: 'error', title: t('CpaClientWorkspace.error'), text: e?.response?.data?.message || t('CpaClientWorkspace.failedToAddFramework') });
    } finally { setSaving(false); }
  };

  const monthlyCost = enrolled.reduce((s, fw) => s + priceFor(fw.display_name || fw.name), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700 }}>{t('CpaClientWorkspace.complianceFrameworks')}</h4>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.87rem' }}>
            {t('CpaClientWorkspace.complianceFrameworksSubtitle', { name: org.name, price: partnerPrice })}
          </p>
        </div>
        {available.length > 0 && (
          <button className="au-create-btn" onClick={() => setShowAdd(true)}>
            <i className="fas fa-plus" /> {t('CpaClientWorkspace.addFramework')}
          </button>
        )}
      </div>

      {/* Partner billing banner */}
      <div style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        border: '1px solid #38bdf8', borderRadius: 12, padding: '14px 18px', marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 700, color: '#0c4a6e' }}>{t('CpaClientWorkspace.partnerBillingForClient')}</div>
          <div style={{ fontSize: '0.83rem', color: '#075985' }}>
            {t('CpaClientWorkspace.frameworksTimesPrice', { count: enrolled.length, price: partnerPrice })}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 800, fontSize: '1.4rem', color: '#0c4a6e' }}>${monthlyCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('CpaClientWorkspace.perMonth')}</div>
        </div>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} /></div>
        : enrolled.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <i className="fas fa-layer-group" style={{ fontSize: 36, marginBottom: 16, display: 'block' }} />
            <p style={{ fontWeight: 600, color: '#64748b' }}>{t('CpaClientWorkspace.noFrameworksEnrolled')}</p>
            <p style={{ fontSize: '0.87rem' }}>{t('CpaClientWorkspace.enrollClientHint')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {enrolled.map(fw => (
              <div key={fw.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                  <i className="fas fa-shield-alt" style={{ marginRight: 8, color: '#3b82f6' }} />
                  {fw.display_name || fw.name}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 6 }}>
                  {t('CpaClientWorkspace.pricePostPaid', { price: priceFor(fw.display_name || fw.name) })}
                </div>
              </div>
            ))}
          </div>
        )}

      {showAdd && (
        <div className="au-overlay" onClick={() => setShowAdd(false)}>
          <div className="au-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="au-modal-header">
              <span><i className="fas fa-plus" /> {t('CpaClientWorkspace.addFramework')}</span>
              <button className="au-modal-close" onClick={() => setShowAdd(false)}><i className="fas fa-times" /></button>
            </div>
            <div className="au-modal-body">
              <div className="au-field"><label>{t('CpaClientWorkspace.frameworkRequired')}</label>
                <select className="au-input" value={newFw} onChange={e => setNewFw(e.target.value)}>
                  <option value="">{t('CpaClientWorkspace.selectFramework')}</option>
                  {available.map(fw => <option key={fw} value={fw}>{fw}</option>)}
                </select></div>
              <div className="au-field"><label>{t('CpaClientWorkspace.auditEngagementFee')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('CpaClientWorkspace.clientInvoice')}</span></label>
                <input className="au-input" type="number" min={0} step={100} placeholder="0"
                  value={newFee} onChange={e => setNewFee(e.target.value)} />
                <p className="au-field-hint">{t('CpaClientWorkspace.oneTimeFeeHint')}</p></div>
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 12px',
                fontSize: '0.83rem', color: '#075985' }}>
                <i className="fas fa-info-circle" /> {t('CpaClientWorkspace.addFrameworkBillingHintBefore')} <strong>{t('CpaClientWorkspace.pricePerMonth', { price: (newFw ? priceFor(newFw) : partnerPrice) })}</strong> {t('CpaClientWorkspace.addFrameworkBillingHintAfter')}
              </div>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-secondary" onClick={() => setShowAdd(false)}>{t('CpaClientWorkspace.cancel')}</button>
              <button className="mp-btn-primary" onClick={handleAdd} disabled={saving || !newFw}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> {t('CpaClientWorkspace.adding')}</> : <><i className="fas fa-plus" /> {t('CpaClientWorkspace.enroll')}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main CpaClientWorkspace ───────────────────────────────────────────────────

const TABS = [
  { key: 'overview',     label: i18n.t('CpaClientWorkspace.tabOverview'),      icon: 'fas fa-layer-group' },
  { key: 'attackSurface',label: i18n.t('CpaClientWorkspace.tabAttackSurface'), icon: 'fas fa-globe' },
  { key: 'audits',       label: i18n.t('CpaClientWorkspace.tabAudits'),        icon: 'fas fa-clipboard-check' },
  { key: 'library',      label: i18n.t('CpaClientWorkspace.tabLibrary'),       icon: 'fas fa-folder-open' },
  { key: 'setup',        label: i18n.t('CpaClientWorkspace.tabSetup'),         icon: 'fas fa-cog' },
];

// Sub-toggle used inside merged tabs.
function SubToggle({ value, onChange, options }) {
  return (
    <div style={{ display: 'inline-flex', background: '#f1f5f9', border: '1px solid #e2e8f0',
      borderRadius: 8, padding: 3, marginBottom: 16 }}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)}
          style={{ padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 12,
            fontWeight: 600, borderRadius: 6,
            background: value === o.value ? '#fff' : 'transparent',
            color: value === o.value ? '#3b82f6' : '#64748b',
            boxShadow: value === o.value ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>
          {o.icon && <i className={o.icon} style={{ marginRight: 6 }} />}{o.label}
        </button>
      ))}
    </div>
  );
}

function AttackSurfaceTab({ org }) {
  const { t } = useTranslation();
  const [view, setView] = useState('external');
  return (
    <div>
      <SubToggle value={view} onChange={setView} options={[
        { value: 'external', label: t('CpaClientWorkspace.externalAsm'),  icon: 'fas fa-globe' },
        { value: 'internal', label: t('CpaClientWorkspace.internalIasm'), icon: 'fas fa-network-wired' },
      ]} />
      {view === 'external' ? <AsmTab org={org} /> : <IasmTab org={org} />}
    </div>
  );
}

function LibraryTab({ org }) {
  const { t } = useTranslation();
  const [view, setView] = useState('policies');
  return (
    <div>
      <SubToggle value={view} onChange={setView} options={[
        { value: 'policies',  label: t('CpaClientWorkspace.policies'),  icon: 'fas fa-file-shield' },
        { value: 'documents', label: t('CpaClientWorkspace.documents'), icon: 'fas fa-file-alt' },
      ]} />
      {view === 'policies' ? <PoliciesTab org={org} /> : <DocumentsTab org={org} />}
    </div>
  );
}

function SetupTab({ org }) {
  const { t } = useTranslation();
  const [view, setView] = useState('integrations');
  return (
    <div>
      <SubToggle value={view} onChange={setView} options={[
        { value: 'integrations', label: t('CpaClientWorkspace.integrations'), icon: 'fas fa-plug' },
        { value: 'team',         label: t('CpaClientWorkspace.team'),         icon: 'fas fa-users' },
      ]} />
      {view === 'integrations' ? <IntegrationsTab org={org} /> : <TeamTab org={org} />}
    </div>
  );
}

// Map legacy initialTab keys to the new consolidated tab keys so callers
// (e.g. notifications, deep-links) keep working without changes.
const LEGACY_TAB_MAP = {
  asm: 'attackSurface', iasm: 'attackSurface',
  frameworks: 'overview',
  integrations: 'setup', team: 'setup',
  documents: 'library', policies: 'library',
  audits: 'audits',
};

export default function CpaClientWorkspace({ org, onClose, initialTab }) {
  const resolved = initialTab && LEGACY_TAB_MAP[initialTab] ? LEGACY_TAB_MAP[initialTab]
                  : (initialTab && TABS.some(t => t.key === initialTab) ? initialTab : 'overview');
  const [tab, setTab] = useState(resolved);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.55)', display: 'flex', flexDirection: 'column' }}
      onClick={onClose}>
      <div style={{ background: '#fff', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden',
        margin: '32px auto', width: '100%', maxWidth: 1100, borderRadius: 16, boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 28px',
          borderBottom: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '16px 16px 0 0' }}>
          {org.logoUrl
            ? <img src={org.logoUrl} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'contain', border: '1px solid #e2e8f0' }} />
            : <div style={{ width: 48, height: 48, borderRadius: 10, background: '#dbeafe', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20, color: '#1e40af' }}>
                {org.name?.[0]?.toUpperCase()}
              </div>
          }
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>{org.name}</h3>
            {org.contactEmail && <div style={{ fontSize: '0.83rem', color: '#64748b' }}>{org.contactEmail}</div>}
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', padding: 6 }}>
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 28px', background: '#fff' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer',
                fontWeight: tab === t.key ? 700 : 500, fontSize: '0.9rem',
                color: tab === t.key ? '#3b82f6' : '#64748b',
                borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
                display: 'flex', alignItems: 'center', gap: 7, marginBottom: -1 }}>
              <i className={t.icon} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
          {tab === 'overview'      && <FrameworksTab org={org} />}
          {tab === 'attackSurface' && <AttackSurfaceTab org={org} />}
          {tab === 'audits'        && <AuditsTab org={org} />}
          {tab === 'library'       && <LibraryTab org={org} />}
          {tab === 'setup'         && <SetupTab org={org} />}
        </div>
      </div>
    </div>
  );
}
